import { Mail, MessageCircle, Phone, Plus } from 'lucide-react';

import { mailtoHref, telHref, whatsappHref } from '../../../lib/quickActions';
import type { CompanyContactRow } from './contacts.schema';
import styles from './contacts.module.css';

function initialsFor(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function RowQuickActions({
  phone,
  email,
}: {
  phone: string | null;
  email: string | null;
}) {
  const wa = whatsappHref(phone);
  const tel = telHref(phone);
  const mailto = mailtoHref(email);
  if (!wa && !tel && !mailto) return null;
  return (
    <span
      className={styles.quickActions}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.quickActionBtn}
          aria-label="פתח WhatsApp"
          title="WhatsApp"
        >
          <MessageCircle size={12} aria-hidden="true" />
        </a>
      ) : null}
      {mailto ? (
        <a
          href={mailto}
          className={styles.quickActionBtn}
          aria-label="שלח אימייל"
          title="אימייל"
        >
          <Mail size={12} aria-hidden="true" />
        </a>
      ) : null}
      {tel ? (
        <a
          href={tel}
          className={styles.quickActionBtn}
          aria-label="התקשר"
          title="חיוג"
        >
          <Phone size={12} aria-hidden="true" />
        </a>
      ) : null}
    </span>
  );
}

interface ContactsListProps {
  contacts: CompanyContactRow[];
  isLoading: boolean;
  isError: boolean;
  onCreate: () => void;
  onEdit: (contact: CompanyContactRow) => void;
}

export function ContactsList({
  contacts,
  isLoading,
  isError,
  onCreate,
  onEdit,
}: ContactsListProps) {
  if (isLoading) {
    return <p className={styles.notice}>טוען אנשי קשר...</p>;
  }

  if (isError) {
    return <p className={styles.notice}>לא הצלחנו לטעון אנשי קשר · נסה שוב</p>;
  }

  return (
    <div className={styles.listWrap}>
      <div className={styles.listHead}>
        <button type="button" className={styles.btnPrimary} onClick={onCreate}>
          <Plus size={15} aria-hidden="true" />
          איש קשר
        </button>
      </div>

      {contacts.length === 0 ? (
        <div className={styles.emptyInline}>
          <p>לא קיימים אנשי קשר · הוסף איש קשר ראשון</p>
          <button type="button" className={styles.btnSecondary} onClick={onCreate}>
            <Plus size={15} aria-hidden="true" />
            איש קשר
          </button>
        </div>
      ) : (
        <div className={styles.cards}>
          {contacts.map((contact) => (
            <div key={contact.id} className={styles.cardWrap}>
              <button
                type="button"
                className={styles.card}
                onClick={() => {
                  onEdit(contact);
                }}
              >
                <span className={styles.avatar} aria-hidden="true">
                  {initialsFor(contact.full_name)}
                </span>

                <span className={styles.cardBody}>
                  <strong>{contact.full_name}</strong>
                  {contact.role_title ? <small>{contact.role_title}</small> : null}
                  {contact.email || contact.phone ? (
                    <span className={styles.meta}>
                      {contact.email ? <bdi>{contact.email}</bdi> : null}
                      {contact.email && contact.phone ? <span aria-hidden="true">·</span> : null}
                      {contact.phone ? <bdi>{contact.phone}</bdi> : null}
                    </span>
                  ) : null}
                </span>
              </button>
              <RowQuickActions phone={contact.phone} email={contact.email} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
